import { useId, type ComponentPropsWithoutRef, type ReactNode, type Ref } from 'react'
import { BAY_PIN_LABEL, CHECK_IN_CODE_HELP } from '@butter/library/simulatorCompanion'
import { cn } from '#utils'
import { KeyIcon, MonitorIcon } from '#icons'
import { CopyButton } from '#primitives/actions'
import { WorksFromNote } from '../WorksFromNote'
import { ACCESS_CODES_TITLE, DOOR_CODE_LABEL } from '../copy'

/** One code on the card, and when it starts working (club time, from the API). */
export interface AccessCodeEntry {
  code: string
  worksFrom?: string | null
}

export interface AccessCodesCardProps extends ComponentPropsWithoutRef<'section'> {
  /** The door code — only while the club's provider is a keypad one; the API decides. */
  doorCode?: AccessCodeEntry | null
  bayPin?: AccessCodeEntry | null
  /**
   * `card`: a card of its own, as the mobile booking screens draw it. `rows`: label-and-pill
   * rows with a copy button, for the web ticket the codes sit inside.
   */
  variant?: 'card' | 'rows'
  /** Prefix for the codes' test ids: `<prefix>-access-code`, `<prefix>-bay-pin`. */
  testIdPrefix?: string
  ref?: Ref<HTMLElement>
}

/**
 * A booking's codes in one place (simulator-companion D77): "Access Codes" with the door code
 * and the Bay PIN, or a lone "Bay PIN" where the club has no door code. Each says when it
 * starts working — the door and the bay open at different times — and the Bay PIN says where it
 * is typed. Renders nothing when there is neither.
 *
 * @example
 * ```tsx
 * <AccessCodesCard
 *   doorCode={{ code: '4689', worksFrom: '2:45 PM' }}
 *   bayPin={{ code: '1234', worksFrom: '2:55 PM' }}
 * />
 * ```
 */
export function AccessCodesCard({
  doorCode,
  bayPin,
  variant = 'card',
  testIdPrefix = 'access-codes',
  className,
  ref,
  ...props
}: AccessCodesCardProps) {
  const titleId = useId()
  if (!doorCode && !bayPin) return null
  const both = Boolean(doorCode && bayPin)
  const entries: Array<CodeRowProps & { key: string }> = []
  if (doorCode) entries.push({ key: 'door', label: DOOR_CODE_LABEL, icon: <KeyIcon className="size-5" />, entry: doorCode, testId: `${testIdPrefix}-access-code` })
  if (bayPin) entries.push({ key: 'bay', label: BAY_PIN_LABEL, icon: <MonitorIcon className="size-5" />, entry: bayPin, testId: `${testIdPrefix}-bay-pin`, help: CHECK_IN_CODE_HELP })

  if (variant === 'rows') {
    return (
      <section ref={ref} aria-label={doorCode ? ACCESS_CODES_TITLE : BAY_PIN_LABEL} className={className} {...props}>
        {entries.map(({ key, ...row }) => <CodeRow key={key} {...row} />)}
      </section>
    )
  }

  return (
    <section
      ref={ref}
      aria-labelledby={titleId}
      className={cn('rounded-18 border-1.25 border-butter-gray-100 px-6 py-5', className)}
      {...props}
    >
      <h2 id={titleId} className="text-sm font-semibold text-fg-primary">
        {doorCode ? ACCESS_CODES_TITLE : BAY_PIN_LABEL}
      </h2>
      <div className="mt-3 flex flex-col gap-4">
        {entries.map(({ key, label, icon, entry, testId, help }) => (
          <div key={key}>
            {/* A lone card already says what the code is in its heading. */}
            {(both || key === 'door') && <p className="mb-2 text-xs font-medium text-fg-tertiary">{label}</p>}
            <div className="flex items-center gap-3 rounded-full border border-butter-gray-200 bg-butter-gray-25 px-4 py-2.5">
              <span className="text-theme-500">{icon}</span>
              <span className="flex-1 text-center text-xl font-bold tracking-widest text-fg-primary">
                <span className="sr-only">{label}: </span>
                <span data-testid={testId}>{spaced(entry.code)}</span>
              </span>
            </div>
            {entry.worksFrom && (
              <p className="mt-1.5 text-xs text-fg-tertiary"><WorksFromNote time={entry.worksFrom} /></p>
            )}
            {help && <p className="mt-1 text-xs text-fg-tertiary">{help}</p>}
          </div>
        ))}
      </div>
    </section>
  )
}

interface CodeRowProps {
  label: string
  icon: ReactNode
  entry: AccessCodeEntry
  testId: string
  /** Where the code is typed, under it — the Bay PIN's. */
  help?: string
}

/** The web ticket's row: label and when it works on the left, the code and a copy button on the right. */
function CodeRow({ label, icon, entry, testId, help }: CodeRowProps) {
  return (
    <div className="py-2 flex justify-between items-center">
      <div>
        <p className="mb-1 text-sm font-medium text-fg-primary">{label}</p>
        {entry.worksFrom && <p className="text-xs text-fg-quaternary"><WorksFromNote time={entry.worksFrom} /></p>}
        {help && <p className="mt-1 text-xs text-fg-quaternary">{help}</p>}
      </div>
      <div className="inline-flex items-center gap-3 text-theme-600 rounded-full border-2 border-theme-200 bg-theme-50 px-4 py-2">
        {icon}
        <p className="whitespace-nowrap text-lg font-extrabold tracking-widest">
          <span className="sr-only">{label}: </span>
          <span data-testid={testId}>{spaced(entry.code)}</span>
        </p>
        <CopyButton text={entry.code} ariaLabel={`Copy ${label}`} />
      </div>
    </div>
  )
}

/** Digits spaced for reading aloud and copying by eye. */
function spaced(code: string): string {
  return code.split('').join(' ')
}
